import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { DashboardLayout } from '../components/layout/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { LicenseUsageBanner, UserStatsCards } from '../components/users';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Separator } from '../components/ui/separator';
import { Checkbox } from '../components/ui/checkbox';
import { Switch } from '../components/ui/switch';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '../components/ui/command';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
  DialogDescription,
} from '../components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { ScrollArea } from '../components/ui/scroll-area';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { useAuth } from '../contexts/AuthContext';
import { useBranding } from '../contexts/BrandingContext';
import { useTenant } from '../contexts/TenantContext';
import { toast } from 'sonner';
import axios from 'axios';
import {
  Users,
  Plus,
  Search,
  Edit,
  Trash2,
  Shield,
  UserPlus,
  Loader2,
  CheckCircle2,
  XCircle,
  Crown,
  Briefcase,
  GraduationCap,
  Store,
  MapPin,
  Upload,
  FileSpreadsheet,
  Download,
  Camera,
  IdCard,
  Phone,
  Calendar,
  User,
  Network,
  Building2,
  Mail,
  Lock,
  ChevronLeft,
  ChevronRight,
  Eye,
  UserCog,
  KeyRound,
  ChevronsUpDown,
  UserCheck,
  X,
} from 'lucide-react';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Gender options
const GENDERS = [
  { id: 'male', name: 'Male' },
  { id: 'female', name: 'Female' },
  { id: 'other', name: 'Other' },
];

// Predefined roles with their descriptions
const ROLES = [
  {
    id: 'admin',
    name: 'Administrator',
    description: 'Full system access',
    icon: Crown,
    color: 'text-yellow-500',
    bgColor: 'bg-yellow-500/10',
  },
  {
    id: 'training_team',
    name: 'L&D Admin',
    description: 'Course management',
    icon: GraduationCap,
    color: 'text-purple-500',
    bgColor: 'bg-purple-500/10',
  },
  {
    id: 'regional_manager',
    name: 'Regional Manager',
    description: 'Regional oversight',
    icon: MapPin,
    color: 'text-green-500',
    bgColor: 'bg-green-500/10',
  },
  {
    id: 'store_manager',
    name: 'Store Manager',
    description: 'Store operations',
    icon: Store,
    color: 'text-blue-500',
    bgColor: 'bg-blue-500/10',
  },
  {
    id: 'cra',
    name: 'Store Executive',
    description: 'Frontline staff',
    icon: Briefcase,
    color: 'text-gray-500',
    bgColor: 'bg-gray-500/10',
  },
];

const ROWS_PER_PAGE = 10;

const UserManagement = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { branding } = useBranding();
  const { licenseInfo, canAddUser, getLicenseLimitMessage, refreshLicenseInfo, isPlatformUser } = useTenant();
  const [users, setUsers] = useState([]);
  const [managers, setManagers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedRole, setSelectedRole] = useState('all');
  const [lastLoginFilter, setLastLoginFilter] = useState('all');
  const [createdOnFilter, setCreatedOnFilter] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  
  // Org Structure data
  const [orgLevels, setOrgLevels] = useState([]);
  const [orgEntries, setOrgEntries] = useState([]);
  const [stores, setStores] = useState([]);
  
  // Dialog states
  const [userDialogOpen, setUserDialogOpen] = useState(false);
  const [bulkDialogOpen, setBulkDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [viewUserDialogOpen, setViewUserDialogOpen] = useState(false);
  
  // Form states
  const [isEditing, setIsEditing] = useState(false);
  const [editingUserId, setEditingUserId] = useState(null);
  const [formStep, setFormStep] = useState(1);
  const [saving, setSaving] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);
  const [viewingUser, setViewingUser] = useState(null);
  
  // Form data
  const [formData, setFormData] = useState({
    // Basic Info
    name: '',
    email: '',
    mobile_number: '',
    employee_id: '',
    avatar: null,
    // Personal Info
    gender: '',
    date_of_birth: '',
    date_of_joining: '',
    // Role & Org
    role: 'cra',
    org_entry_id: '',
    assigned_store_ids: [],
    manager_id: '',
    // Settings
    is_active: true,
    send_welcome_email: true,
    otp_login_enabled: false,
  });
  
  // Real-time validation state
  const [validation, setValidation] = useState({
    email: { checking: false, available: null, message: '' },
    mobile: { checking: false, available: null, message: '' },
    employee_id: { checking: false, available: null, message: '' },
  });
  const validationTimeoutRef = useRef({});
  
  // License Usage State (for multi-tenant) - now using TenantContext
  const [licenseUsage, setLicenseUsage] = useState(null);
  const [licenseLoading, setLicenseLoading] = useState(false);
  
  const [avatarPreview, setAvatarPreview] = useState(null);
  const avatarInputRef = useRef(null);
  
  // Reset Password
  const [resetPasswordDialogOpen, setResetPasswordDialogOpen] = useState(false);
  const [userToReset, setUserToReset] = useState(null);
  const [resettingPassword, setResettingPassword] = useState(false);
  
  // Bulk Import
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const fileInputRef = useRef(null);

  // Main Tab State
  const [mainTab, setMainTab] = useState('users');

  // Roles Management State
  const [roles, setRoles] = useState([]);
  const [rolesLoading, setRolesLoading] = useState(false);
  const [roleDialogOpen, setRoleDialogOpen] = useState(false);
  const [editingRole, setEditingRole] = useState(null);
  const [roleFormData, setRoleFormData] = useState({
    value: '',
    label: '',
    description: '',
    permissions: {},
  });

  // Define all available modules and their permissions
  const PERMISSION_MODULES = [
    {
      id: 'dashboard',
      name: 'Dashboard',
      description: 'View dashboard and analytics',
      permissions: [
        { id: 'view', name: 'View', description: 'Access dashboard' },
      ],
    },
    {
      id: 'user_management',
      name: 'User Management',
      description: 'Manage users and roles',
      permissions: [
        { id: 'view', name: 'View', description: 'View user list' },
        { id: 'create', name: 'Create', description: 'Create new users' },
        { id: 'edit', name: 'Edit', description: 'Edit user details' },
        { id: 'delete', name: 'Delete', description: 'Delete users' },
        { id: 'reset_password', name: 'Reset Password', description: 'Reset user passwords' },
        { id: 'bulk_import', name: 'Bulk Import', description: 'Import users via CSV' },
      ],
    },
    {
      id: 'course_management',
      name: 'Course Management',
      description: 'Manage courses and content',
      permissions: [
        { id: 'view', name: 'View', description: 'View courses' },
        { id: 'create', name: 'Create', description: 'Create new courses' },
        { id: 'edit', name: 'Edit', description: 'Edit course content' },
        { id: 'delete', name: 'Delete', description: 'Delete courses' },
        { id: 'publish', name: 'Publish', description: 'Publish/unpublish courses' },
        { id: 'assign', name: 'Assign', description: 'Assign courses to users/roles' },
      ],
    },
    {
      id: 'quiz_management',
      name: 'Quiz Management',
      description: 'Manage quizzes and assessments',
      permissions: [
        { id: 'view', name: 'View', description: 'View quizzes' },
        { id: 'create', name: 'Create', description: 'Create quizzes' },
        { id: 'edit', name: 'Edit', description: 'Edit quiz questions' },
        { id: 'delete', name: 'Delete', description: 'Delete quizzes' },
        { id: 'view_results', name: 'View Results', description: 'View quiz results' },
      ],
    },
    {
      id: 'reports',
      name: 'Reports & Analytics',
      description: 'Access reports and analytics',
      permissions: [
        { id: 'view', name: 'View', description: 'View reports' },
        { id: 'export', name: 'Export', description: 'Export report data' },
        { id: 'team_progress', name: 'Team Progress', description: 'View team progress' },
      ],
    },
    {
      id: 'org_structure',
      name: 'Org Structure',
      description: 'Manage organizational hierarchy',
      permissions: [
        { id: 'view', name: 'View', description: 'View org structure' },
        { id: 'manage', name: 'Manage', description: 'Create/edit org entries' },
      ],
    },
    {
      id: 'settings',
      name: 'Admin Settings',
      description: 'System configuration',
      permissions: [
        { id: 'view', name: 'View', description: 'View settings' },
        { id: 'manage', name: 'Manage', description: 'Change settings' },
      ],
    },
    {
      id: 'ai_features',
      name: 'AI Features',
      description: 'AI-powered features',
      permissions: [
        { id: 'video_generation', name: 'Video Generation', description: 'Generate AI videos' },
        { id: 'quiz_generation', name: 'Quiz Generation', description: 'Generate AI quizzes' },
        { id: 'translation', name: 'Translation', description: 'Translate content' },
      ],
    },
  ];

  useEffect(() => {
    fetchAllData();
    fetchRoles(); // Fetch roles on mount for user form dropdown
    
    // Refresh license info when page loads or becomes visible
    if (!isPlatformUser) {
      refreshLicenseInfo();
    }
  }, []);

  // Sync licenseUsage from TenantContext
  useEffect(() => {
    if (licenseInfo) {
      setLicenseUsage(licenseInfo);
    }
  }, [licenseInfo]);

  useEffect(() => {
    if (mainTab === 'roles') {
      fetchRoles();
    }
  }, [mainTab]);

  const fetchRoles = async () => {
    setRolesLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/roles`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setRoles(response.data || []);
    } catch (error) {
      console.error('Failed to fetch roles:', error);
      // Fallback to hardcoded roles if API fails
      setRoles(ROLES.map(r => ({ value: r.id, label: r.name, description: r.description })));
    } finally {
      setRolesLoading(false);
    }
  };

  const fetchAllData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      
      const [usersRes, managersRes, levelsRes, entriesRes, storesRes] = await Promise.all([
        axios.get(`${API_URL}/admin/users`, { headers }),
        axios.get(`${API_URL}/admin/users/managers`, { headers }),
        axios.get(`${API_URL}/org/levels`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${API_URL}/org/entries`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${API_URL}/org/stores`, { headers }).catch(() => ({ data: [] })),
      ]);
      
      setUsers(usersRes.data);
      setManagers(managersRes.data);
      setOrgLevels(levelsRes.data);
      setOrgEntries(entriesRes.data);
      setStores(storesRes.data);
    } catch (error) {
      console.error('Failed to fetch data:', error);
      toast.error('Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  // Real-time validation functions
  const validateEmail = useCallback(async (email, excludeUserId = null) => {
    if (!email || email.length < 5 || !email.includes('@')) {
      setValidation(prev => ({ ...prev, email: { checking: false, available: null, message: '' } }));
      return;
    }

    // Clear existing timeout
    if (validationTimeoutRef.current.email) {
      clearTimeout(validationTimeoutRef.current.email);
    }

    setValidation(prev => ({ ...prev, email: { checking: true, available: null, message: 'Checking...' } }));

    validationTimeoutRef.current.email = setTimeout(async () => {
      try {
        const token = localStorage.getItem('token');
        const params = new URLSearchParams({ email });
        if (excludeUserId) params.append('exclude_user_id', excludeUserId);
        
        const res = await axios.get(`${API_URL}/admin/users/validate-email?${params}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setValidation(prev => ({ 
          ...prev, 
          email: { 
            checking: false, 
            available: res.data.available, 
            message: res.data.message 
          } 
        }));
      } catch (error) {
        setValidation(prev => ({ ...prev, email: { checking: false, available: null, message: '' } }));
      }
    }, 500); // Debounce 500ms
  }, []);

  const validateMobile = useCallback(async (mobile, excludeUserId = null) => {
    if (!mobile || mobile.length < 10) {
      setValidation(prev => ({ ...prev, mobile: { checking: false, available: null, message: '' } }));
      return;
    }

    if (validationTimeoutRef.current.mobile) {
      clearTimeout(validationTimeoutRef.current.mobile);
    }

    setValidation(prev => ({ ...prev, mobile: { checking: true, available: null, message: 'Checking...' } }));

    validationTimeoutRef.current.mobile = setTimeout(async () => {
      try {
        const token = localStorage.getItem('token');
        const params = new URLSearchParams({ mobile });
        if (excludeUserId) params.append('exclude_user_id', excludeUserId);
        
        const res = await axios.get(`${API_URL}/admin/users/validate-mobile?${params}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setValidation(prev => ({ 
          ...prev, 
          mobile: { 
            checking: false, 
            available: res.data.available, 
            message: res.data.message 
          } 
        }));
      } catch (error) {
        setValidation(prev => ({ ...prev, mobile: { checking: false, available: null, message: '' } }));
      }
    }, 500);
  }, []);

  const validateEmployeeId = useCallback(async (employeeId, excludeUserId = null) => {
    if (!employeeId || employeeId.length < 1) {
      setValidation(prev => ({ ...prev, employee_id: { checking: false, available: null, message: '' } }));
      return;
    }

    if (validationTimeoutRef.current.employee_id) {
      clearTimeout(validationTimeoutRef.current.employee_id);
    }

    setValidation(prev => ({ ...prev, employee_id: { checking: true, available: null, message: 'Checking...' } }));

    validationTimeoutRef.current.employee_id = setTimeout(async () => {
      try {
        const token = localStorage.getItem('token');
        const params = new URLSearchParams({ employee_id: employeeId });
        if (excludeUserId) params.append('exclude_user_id', excludeUserId);
        
        const res = await axios.get(`${API_URL}/admin/users/validate-employee-id?${params}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setValidation(prev => ({ 
          ...prev, 
          employee_id: { 
            checking: false, 
            available: res.data.available, 
            message: res.data.message 
          } 
        }));
      } catch (error) {
        setValidation(prev => ({ ...prev, employee_id: { checking: false, available: null, message: '' } }));
      }
    }, 500);
  }, []);

  // Reset validation when dialog closes
  const resetValidation = useCallback(() => {
    setValidation({
      email: { checking: false, available: null, message: '' },
      mobile: { checking: false, available: null, message: '' },
      employee_id: { checking: false, available: null, message: '' },
    });
    Object.values(validationTimeoutRef.current).forEach(t => clearTimeout(t));
  }, []);

  // Role Management Handlers
  const openRoleDialog = (role = null) => {
    if (role) {
      setEditingRole(role);
      setRoleFormData({
        value: role.value,
        label: role.label,
        description: role.description || '',
        permissions: role.permissions || {},
      });
    } else {
      setEditingRole(null);
      setRoleFormData({
        value: '',
        label: '',
        description: '',
        permissions: {},
      });
    }
    setRoleDialogOpen(true);
  };

  const handleModulePermissionChange = (moduleId, checked) => {
    const module = PERMISSION_MODULES.find(m => m.id === moduleId);
    if (!module) return;

    setRoleFormData(prev => {
      const newPermissions = { ...prev.permissions };
      if (checked) {
        // Enable all permissions for this module
        newPermissions[moduleId] = module.permissions.map(p => p.id);
      } else {
        // Disable all permissions for this module
        delete newPermissions[moduleId];
      }
      return { ...prev, permissions: newPermissions };
    });
  };

  const handleFeaturePermissionChange = (moduleId, permissionId, checked) => {
    setRoleFormData(prev => {
      const newPermissions = { ...prev.permissions };
      const modulePermissions = newPermissions[moduleId] || [];
      
      if (checked) {
        newPermissions[moduleId] = [...new Set([...modulePermissions, permissionId])];
      } else {
        newPermissions[moduleId] = modulePermissions.filter(p => p !== permissionId);
        if (newPermissions[moduleId].length === 0) {
          delete newPermissions[moduleId];
        }
      }
      
      return { ...prev, permissions: newPermissions };
    });
  };

  const isModuleEnabled = (moduleId) => {
    return roleFormData.permissions[moduleId]?.length > 0;
  };

  const isFeatureEnabled = (moduleId, permissionId) => {
    return roleFormData.permissions[moduleId]?.includes(permissionId);
  };

  const saveRole = async () => {
    if (!roleFormData.label) {
      toast.error('Role name is required');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const payload = {
        value: editingRole ? editingRole.value : roleFormData.value || roleFormData.label.toLowerCase().replace(/\s+/g, '_'),
        label: roleFormData.label,
        description: roleFormData.description,
        permissions: roleFormData.permissions,
      };

      if (editingRole) {
        await axios.put(`${API_URL}/roles/${editingRole.value}`, payload, {
          headers: { Authorization: `Bearer ${token}` },
        });
        toast.success('Role updated successfully');
      } else {
        await axios.post(`${API_URL}/roles`, payload, {
          headers: { Authorization: `Bearer ${token}` },
        });
        toast.success('Role created successfully');
      }

      setRoleDialogOpen(false);
      fetchRoles();
    } catch (error) {
      const detail = error.response?.data?.detail;
      let errorMsg = 'Failed to save role';
      if (typeof detail === 'string') {
        errorMsg = detail;
      } else if (Array.isArray(detail)) {
        // FastAPI 422 validation errors return an array of objects
        errorMsg = detail.map(d => d?.msg || JSON.stringify(d)).join('; ');
      } else if (detail && typeof detail === 'object') {
        errorMsg = detail.msg || JSON.stringify(detail);
      }
      toast.error(errorMsg);
    }
  };

  const deleteRole = async (roleValue) => {
    if (!window.confirm('Are you sure you want to delete this role? Users with this role will need to be reassigned.')) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API_URL}/roles/${roleValue}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success('Role deleted successfully');
      fetchRoles();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete role');
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      email: '',
      mobile_number: '',
      employee_id: '',
      avatar: null,
      gender: '',
      date_of_birth: '',
      date_of_joining: '',
      role: 'cra',
      org_entry_id: '',
      assigned_store_ids: [],
      manager_id: '',
      is_active: true,
      send_welcome_email: true,
      otp_login_enabled: false,
    });
    setAvatarPreview(null);
    setFormStep(1);
    setIsEditing(false);
    setEditingUserId(null);
  };

  const openCreateDialog = () => {
    resetForm();
    resetValidation();
    setUserDialogOpen(true);
  };

  const openEditDialog = (userToEdit) => {
    setIsEditing(true);
    setEditingUserId(userToEdit.id);
    resetValidation();
    
    // Normalize gender to lowercase to match form options ('male', 'female', 'other')
    const normalizedGender = (userToEdit.gender || '').toLowerCase().trim();
    // Normalize mobile number - ensure it's a string and handle various formats
    const normalizedMobile = String(userToEdit.mobile_number || userToEdit.mobile || '').replace(/\D/g, '');
    
    setFormData({
      name: userToEdit.name || '',
      email: userToEdit.email || '',
      mobile_number: normalizedMobile,
      employee_id: userToEdit.employee_id || '',
      avatar: userToEdit.avatar || null,
      gender: normalizedGender,
      date_of_birth: userToEdit.date_of_birth || '',
      date_of_joining: userToEdit.date_of_joining || '',
      role: userToEdit.role || 'cra',
      org_entry_id: userToEdit.org_entry_id || '',
      assigned_store_ids: userToEdit.assigned_store_ids || [],
      manager_id: userToEdit.manager_id || '',
      is_active: userToEdit.is_active !== false,
      send_welcome_email: false, // Don't send email when editing
      otp_login_enabled: userToEdit.otp_login_enabled || false,
    });
    setAvatarPreview(userToEdit.avatar);
    setFormStep(1);
    setUserDialogOpen(true);
  };

  const handleAvatarChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    // Preview
    const reader = new FileReader();
    reader.onload = (e) => setAvatarPreview(e.target?.result);
    reader.readAsDataURL(file);
    
    // Upload
    try {
      const formDataUpload = new FormData();
      formDataUpload.append('file', file);
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API_URL}/upload/image`, formDataUpload, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' },
      });
      setFormData(prev => ({ ...prev, avatar: `${process.env.REACT_APP_BACKEND_URL}${response.data.url}` }));
    } catch (error) {
      toast.error('Failed to upload image');
    }
  };

  const handleStoreToggle = (storeId) => {
    setFormData(prev => ({
      ...prev,
      assigned_store_ids: prev.assigned_store_ids.includes(storeId)
        ? prev.assigned_store_ids.filter(id => id !== storeId)
        : [...prev.assigned_store_ids, storeId]
    }));
  };

  const validateStep = (step) => {
    if (step === 1) {
      if (!formData.name.trim()) {
        toast.error('Name is required');
        return false;
      }
      // Mobile number is mandatory
      if (!formData.mobile_number || formData.mobile_number.length !== 10) {
        toast.error('Valid 10-digit mobile number is required');
        return false;
      }
      // Email is required and must be valid
      if (!formData.email.trim()) {
        toast.error('Email is required');
        return false;
      }
      if (!formData.email.includes('@')) {
        toast.error('Please enter a valid email address');
        return false;
      }
    }
    return true;
  };

  // Reset Password Handler
  const handleResetPassword = async () => {
    if (!userToReset) return;
    setResettingPassword(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API_URL}/admin/users/${userToReset.id}/reset-password`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (response.data.email_sent) {
        toast.success(`Password reset! New temporary password sent to ${userToReset.email}`);
      } else {
        toast.success('Password reset! (Email not sent - SMTP not configured)');
      }
      setResetPasswordDialogOpen(false);
      setUserToReset(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reset password');
    } finally {
      setResettingPassword(false);
    }
  };

  const handleSaveUser = async () => {
    if (!validateStep(1)) return;
    
    // Check real-time validation results
    if (validation.email.available === false) {
      toast.error('Please use a different email address');
      return;
    }
    if (validation.mobile.available === false) {
      toast.error('Please use a different mobile number');
      return;
    }
    if (validation.employee_id.available === false) {
      toast.error('Please use a different employee ID');
      return;
    }
    
    // Check license limits before creating new user
    if (!isEditing && !isPlatformUser) {
      // Use TenantContext for license check
      if (!canAddUser(formData.role)) {
        const licenseMessage = getLicenseLimitMessage(formData.role);
        toast.error(licenseMessage || 'License limit reached. Contact sales to add more licenses.');
        return;
      }
    }
    
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };
      
      const payload = { ...formData };
      // Clean up empty strings
      if (!payload.org_entry_id) delete payload.org_entry_id;
      if (!payload.manager_id) delete payload.manager_id;
      
      if (isEditing) {
        await axios.put(`${API_URL}/admin/users/${editingUserId}`, payload, { headers });
        toast.success('User updated successfully');
      } else {
        const response = await axios.post(`${API_URL}/admin/users`, payload, { headers });
        if (response.data.email_sent) {
          toast.success('User created! Welcome email sent with login credentials.');
        } else if (payload.send_welcome_email) {
          toast.success('User created! (Email not sent - SMTP not configured)');
        } else {
          toast.success('User created successfully');
        }
      }
      
      setUserDialogOpen(false);
      resetForm();
      resetValidation();
      fetchAllData();
      refreshLicenseInfo(); // Refresh license usage after user creation via TenantContext
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save user');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteUser = async () => {
    if (!userToDelete) return;
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API_URL}/admin/users/${userToDelete.id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success('User deleted successfully');
      setDeleteDialogOpen(false);
      setUserToDelete(null);
      fetchAllData();
      refreshLicenseInfo(); // Refresh license after deletion
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete user');
    }
  };

  const handleBulkImport = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    setImporting(true);
    setImportResult(null);
    
    try {
      const formDataUpload = new FormData();
      formDataUpload.append('file', file);
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API_URL}/admin/users/bulk-import`, formDataUpload, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' },
      });
      setImportResult(response.data);
      
      // Show appropriate toast message based on results
      const createdCount = response.data.created || 0;
      const updatedCount = response.data.updated || 0;
      const failedCount = response.data.failed || 0;
      if (createdCount > 0 && updatedCount > 0) {
        toast.success(`Imported ${createdCount} new, updated ${updatedCount} existing users`);
      } else if (createdCount > 0) {
        toast.success(`Successfully imported ${createdCount} users`);
      } else if (updatedCount > 0) {
        toast.success(`Updated ${updatedCount} existing users`);
      } else if (failedCount > 0) {
        toast.warning(`Import completed with ${failedCount} failed entries`);
      }
      
      // Always refresh the user list after bulk import completes (regardless of created count)
      // This ensures the UI stays in sync even if users were updated or duplicates skipped
      await fetchAllData();
      refreshLicenseInfo(); // Refresh license usage after bulk import
      
      // Auto-close dialog after successful import (created or updated)
      if (createdCount > 0 || updatedCount > 0) {
        setTimeout(() => setBulkDialogOpen(false), 2000);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to import users');
    } finally {
      setImporting(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const downloadCSVTemplate = () => {
    const headers = [
      'Employee ID', 'Name', 'Email', 'Password', 'Role', 
      'Mobile', 'Gender', 'DOB', 'DOJ',
      'Position', 'Reports To', 'Store Codes', 'OTP Enabled'
    ];
    const sampleRows = [
      ['EMP001', 'John Doe', 'john@company.com', 'password123', 'cra', '9876543210', 'male', '1990-01-15', '2024-01-01', 'RET-001', 'manager@company.com', 'ST001,ST002', 'no'],
      ['EMP002', 'Jane Smith', 'jane@company.com', 'password123', 'store_manager', '9876543211', 'female', '1985-06-20', '2023-06-15', 'OPE-001', 'EMP001', 'ST003', 'yes'],
    ];
    let csvContent = headers.join(',') + '\n';
    sampleRows.forEach(row => csvContent += row.join(',') + '\n');
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'user_import_template.csv';
    a.click();
    window.URL.revokeObjectURL(url);
  };

  // Get role info from dynamic roles or fallback to ROLES
  const getRoleInfo = (roleId) => {
    // First try to find in dynamic roles from API
    const dynamicRole = roles.find(r => r.value === roleId);
    if (dynamicRole) {
      return {
        id: dynamicRole.value,
        name: dynamicRole.label,
        description: dynamicRole.description || '',
        icon: Shield,
        color: 'text-gray-500',
      };
    }
    // Fallback to hardcoded ROLES
    return ROLES.find(r => r.id === roleId) || { id: roleId, name: roleId, icon: Shield, color: 'text-gray-500' };
  };
  
  const getOrgEntryName = (entryId) => {
    const entry = orgEntries.find(e => e.id === entryId);
    return entry ? entry.entry_name : null;
  };
  
  const getAssignedStoreCount = (storeIds) => storeIds?.length || 0;

  // Filtering and pagination
  const isWithinDays = (isoString, days) => {
    if (!isoString) return false;
    const ts = new Date(isoString).getTime();
    if (Number.isNaN(ts)) return false;
    return (Date.now() - ts) <= days * 24 * 60 * 60 * 1000;
  };
  
  const filteredUsers = users.filter(u => {
    const matchesSearch = u.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      u.email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      u.employee_id?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesRole = selectedRole === 'all' || u.role === selectedRole;
    
    // Last login filter
    let matchesLastLogin = true;
    if (lastLoginFilter === 'never') matchesLastLogin = !u.last_login;
    else if (lastLoginFilter === '7d') matchesLastLogin = isWithinDays(u.last_login, 7);
    else if (lastLoginFilter === '30d') matchesLastLogin = isWithinDays(u.last_login, 30);
    else if (lastLoginFilter === '90d') matchesLastLogin = isWithinDays(u.last_login, 90);
    else if (lastLoginFilter === '>30d') matchesLastLogin = !!u.last_login && !isWithinDays(u.last_login, 30);
    else if (lastLoginFilter === '>90d') matchesLastLogin = !!u.last_login && !isWithinDays(u.last_login, 90);
    
    // Created on filter
    let matchesCreated = true;
    if (createdOnFilter === '7d') matchesCreated = isWithinDays(u.created_at, 7);
    else if (createdOnFilter === '30d') matchesCreated = isWithinDays(u.created_at, 30);
    else if (createdOnFilter === '90d') matchesCreated = isWithinDays(u.created_at, 90);
    else if (createdOnFilter === '>90d') matchesCreated = !!u.created_at && !isWithinDays(u.created_at, 90);
    
    return matchesSearch && matchesRole && matchesLastLogin && matchesCreated;
  });

  const totalPages = Math.ceil(filteredUsers.length / ROWS_PER_PAGE);
  const paginatedUsers = filteredUsers.slice(
    (currentPage - 1) * ROWS_PER_PAGE,
    currentPage * ROWS_PER_PAGE
  );

  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, selectedRole, lastLoginFilter, createdOnFilter]);

  if (user?.role !== 'admin' && user?.role !== 'training_team') {
    return (
      <DashboardLayout>
        <Card className="p-12 text-center">
          <XCircle className="w-16 h-16 mx-auto text-destructive mb-4" />
          <h2 className="text-xl font-semibold mb-2">Access Denied</h2>
          <p className="text-muted-foreground">Only administrators can access this panel.</p>
        </Card>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6 pb-20 lg:pb-0" data-testid="user-management">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <Users className="w-7 h-7" style={{ color: branding.primary_color }} />
              User Management
            </h1>
            <p className="text-muted-foreground">Manage users, roles, and permissions</p>
          </div>
        </div>

        {/* Main Tabs */}
        <Tabs value={mainTab} onValueChange={setMainTab}>
          <TabsList className="grid w-full max-w-md grid-cols-2">
            <TabsTrigger value="users" data-testid="tab-users">
              <Users className="w-4 h-4 mr-2" />
              Users
            </TabsTrigger>
            <TabsTrigger value="roles" data-testid="tab-roles">
              <Shield className="w-4 h-4 mr-2" />
              Roles
            </TabsTrigger>
          </TabsList>

          {/* Users Tab */}
          <TabsContent value="users" className="mt-6 space-y-6">
            {/* License Usage Banner Component (for tenant users only) */}
            {licenseUsage && !isPlatformUser && (
              <LicenseUsageBanner licenseUsage={licenseUsage} />
            )}

            {/* Users Action Bar */}
            <div className="flex justify-end gap-2 flex-wrap">
              {/* Bulk Import */}
              <Dialog open={bulkDialogOpen} onOpenChange={setBulkDialogOpen}>
                <DialogTrigger asChild>
                  <Button variant="outline">
                    <Upload className="w-4 h-4 mr-2" />
                    Import
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-lg">
                <DialogHeader>
                  <DialogTitle>Bulk Import Users</DialogTitle>
                  <DialogDescription>Upload a CSV file to import multiple users with all details</DialogDescription>
                </DialogHeader>
                <div className="space-y-4 py-4">
                  <div className="border-2 border-dashed rounded-lg p-6 text-center">
                    <FileSpreadsheet className="w-12 h-12 mx-auto text-muted-foreground mb-3" />
                    <div className="text-sm text-muted-foreground mb-4 space-y-1">
                      <p className="font-medium text-foreground">CSV Columns:</p>
                      <p><strong>Basic:</strong> Employee ID, Name*, Mobile, Email*, Password, Gender, DOB, DOJ</p>
                      <p><strong>Role & Org:</strong> Role, Position, Reports To, Store Codes, OTP Enabled</p>
                      <p className="text-xs mt-2">* Required fields. Existing users (matched by Email) will be UPDATED — password optional on update. Position &amp; Reports To are optional; if provided, must match a valid Position code/name or Manager email/employee ID. Store Codes can be comma-separated (e.g., ST001,ST002).</p>
                    </div>
                    <input
                      type="file"
                      accept=".csv"
                      onChange={handleBulkImport}
                      ref={fileInputRef}
                      className="hidden"
                    />
                    <div className="flex gap-2 justify-center">
                      <Button variant="outline" size="sm" onClick={downloadCSVTemplate}>
                        <Download className="w-4 h-4 mr-2" />
                        Template
                      </Button>
                      <Button size="sm" onClick={() => fileInputRef.current?.click()} disabled={importing}>
                        {importing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
                        Upload
                      </Button>
                    </div>
                  </div>
                  {importResult && (
                    <div className="bg-muted rounded-lg p-4">
                      <div className="flex gap-6 justify-center flex-wrap">
                        <div className="text-center">
                          <p className="text-2xl font-bold">{importResult.total}</p>
                          <p className="text-xs text-muted-foreground">Total</p>
                        </div>
                        <div className="text-center">
                          <p className="text-2xl font-bold text-green-500">{importResult.created}</p>
                          <p className="text-xs text-muted-foreground">Created</p>
                        </div>
                        <div className="text-center">
                          <p className="text-2xl font-bold text-blue-500">{importResult.updated || 0}</p>
                          <p className="text-xs text-muted-foreground">Updated</p>
                        </div>
                        <div className="text-center">
                          <p className="text-2xl font-bold text-red-500">{importResult.failed}</p>
                          <p className="text-xs text-muted-foreground">Failed</p>
                        </div>
                      </div>
                      {importResult.errors && importResult.errors.length > 0 && (
                        <div className="mt-3 max-h-40 overflow-y-auto border-t pt-2">
                          <p className="text-xs font-semibold text-red-600 mb-1">Errors:</p>
                          <ul className="text-xs space-y-1 text-red-500">
                            {importResult.errors.slice(0, 50).map((err, idx) => (
                              <li key={idx} data-testid={`bulk-import-error-${idx}`}>
                                Row {err.row}: {err.error}
                              </li>
                            ))}
                            {importResult.errors.length > 50 && (
                              <li className="text-muted-foreground">…and {importResult.errors.length - 50} more</li>
                            )}
                          </ul>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </DialogContent>
            </Dialog>

            {/* Create User */}
            <Button onClick={openCreateDialog} data-testid="create-user-btn">
              <UserPlus className="w-4 h-4 mr-2" />
              Add User
            </Button>
          </div>

            {/* Stats Cards Component */}
            <UserStatsCards users={users} branding={branding} />

        {/* Users Table Card */}
        <Card>
          <CardHeader className="pb-3">
            <div className="flex flex-col md:flex-row gap-4">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  placeholder="Search by name, email, or employee ID..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10"
                />
              </div>
              <Select value={selectedRole} onValueChange={setSelectedRole}>
                <SelectTrigger className="w-full md:w-44" data-testid="filter-role">
                  <SelectValue placeholder="Filter by role" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Roles</SelectItem>
                  {roles.length > 0 ? (
                    roles.map((role) => (
                      <SelectItem key={role.value} value={role.value}>{role.label}</SelectItem>
                    ))
                  ) : (
                    ROLES.map((role) => (
                      <SelectItem key={role.id} value={role.id}>{role.name}</SelectItem>
                    ))
                  )}
                </SelectContent>
              </Select>
              <Select value={lastLoginFilter} onValueChange={setLastLoginFilter}>
                <SelectTrigger className="w-full md:w-44" data-testid="filter-last-login">
                  <SelectValue placeholder="Filter by last login" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Any Last Login</SelectItem>
                  <SelectItem value="7d">Last 7 days</SelectItem>
                  <SelectItem value="30d">Last 30 days</SelectItem>
                  <SelectItem value="90d">Last 90 days</SelectItem>
                  <SelectItem value=">30d">Inactive &gt; 30 days</SelectItem>
                  <SelectItem value=">90d">Inactive &gt; 90 days</SelectItem>
                  <SelectItem value="never">Never logged in</SelectItem>
                </SelectContent>
              </Select>
              <Select value={createdOnFilter} onValueChange={setCreatedOnFilter}>
                <SelectTrigger className="w-full md:w-44" data-testid="filter-created-on">
                  <SelectValue placeholder="Filter by created on" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Any Created Date</SelectItem>
                  <SelectItem value="7d">Last 7 days</SelectItem>
                  <SelectItem value="30d">Last 30 days</SelectItem>
                  <SelectItem value="90d">Last 90 days</SelectItem>
                  <SelectItem value=">90d">Older than 90 days</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
              </div>
            ) : paginatedUsers.length > 0 ? (
              <>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>{t('management.user', 'User')}</TableHead>
                      <TableHead>{t('management.employeeId', 'Employee ID')}</TableHead>
                      <TableHead>{t('common.role', 'Role')}</TableHead>
                      <TableHead>{t('common.status', 'Status')}</TableHead>
                      <TableHead>{t('management.createdOn', 'Created On')}</TableHead>
                      <TableHead>{t('management.lastLogin', 'Last Login')}</TableHead>
                      <TableHead className="w-24">{t('common.actions', 'Actions')}</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {paginatedUsers.map((u) => {
                      const roleInfo = getRoleInfo(u.role);
                      const isActive = u.is_active !== false;
                      return (
                        <TableRow key={u.id} className={!isActive ? 'opacity-60' : ''}>
                          <TableCell>
                            <div className="flex items-center gap-3">
                              <Avatar className="h-9 w-9">
                                <AvatarImage src={u.avatar} />
                                <AvatarFallback className={roleInfo.bgColor}>
                                  {u.name?.charAt(0)?.toUpperCase() || 'U'}
                                </AvatarFallback>
                              </Avatar>
                              <div>
                                <p className="font-medium">{u.name}</p>
                                <p className="text-xs text-muted-foreground">{u.email}</p>
                              </div>
                            </div>
                          </TableCell>
                          <TableCell>
                            <span className="font-mono text-sm">{u.employee_id || '-'}</span>
                          </TableCell>
                          <TableCell>
                            <Badge variant="outline" className={`${roleInfo.bgColor} border-0`}>
                              <roleInfo.icon className={`w-3 h-3 mr-1 ${roleInfo.color}`} />
                              {roleInfo.name}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <Badge 
                              variant={isActive ? 'default' : 'secondary'}
                              className={isActive ? 'bg-green-500/20 text-green-700 border-0' : 'bg-gray-500/20 text-gray-500 border-0'}
                            >
                              {isActive ? t('common.active', 'Active') : t('common.inactive', 'Inactive')}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <span className="text-sm text-muted-foreground">
                              {u.created_at ? new Date(u.created_at).toLocaleDateString() : '-'}
                            </span>
                          </TableCell>
                          <TableCell>
                            <span className="text-sm text-muted-foreground">
                              {u.last_login ? new Date(u.last_login).toLocaleDateString() : t('management.never', 'Never')}
                            </span>
                          </TableCell>
                          <TableCell>
                            <div className="flex gap-1">
                              <Button variant="ghost" size="sm" onClick={() => {
                                setViewingUser(u);
                                setViewUserDialogOpen(true);
                              }}>
                                <Eye className="w-4 h-4" />
                              </Button>
                              <Button variant="ghost" size="sm" onClick={() => openEditDialog(u)}>
                                <Edit className="w-4 h-4" />
                              </Button>
                              <Button 
                                variant="ghost" 
                                size="sm" 
                                className="text-amber-600"
                                onClick={() => {
                                  setUserToReset(u);
                                  setResetPasswordDialogOpen(true);
                                }}
                                title="Reset Password"
                              >
                                <KeyRound className="w-4 h-4" />
                              </Button>
                              <Button variant="ghost" size="sm" className="text-destructive" onClick={() => {
                                setUserToDelete(u);
                                setDeleteDialogOpen(true);
                              }}>
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
                
                {/* Pagination */}
                {filteredUsers.length > ROWS_PER_PAGE && (
                  <div className="flex items-center justify-between mt-4 pt-4 border-t">
                    <p className="text-sm text-muted-foreground">
                      Showing {((currentPage - 1) * ROWS_PER_PAGE) + 1} to {Math.min(currentPage * ROWS_PER_PAGE, filteredUsers.length)} of {filteredUsers.length} users
                    </p>
                    <div className="flex items-center gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                        disabled={currentPage === 1}
                      >
                        <ChevronLeft className="w-4 h-4" />
                        Previous
                      </Button>
                      <span className="text-sm px-2">Page {currentPage} of {totalPages}</span>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                        disabled={currentPage === totalPages}
                      >
                        Next
                        <ChevronRight className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                )}
              </>
            ) : (
              <div className="text-center py-12">
                <Users className="w-12 h-12 mx-auto text-muted-foreground mb-3" />
                <p className="text-muted-foreground">No users found</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Create/Edit User Dialog - Compact Multi-Step */}
        <Dialog open={userDialogOpen} onOpenChange={(open) => {
          if (!open) resetForm();
          setUserDialogOpen(open);
        }}>
          <DialogContent className="max-w-2xl max-h-[90vh] flex flex-col">
            <DialogHeader className="pb-2 flex-shrink-0">
              <DialogTitle className="flex items-center gap-2">
                <UserCog className="w-5 h-5" style={{ color: branding.primary_color }} />
                {isEditing ? 'Edit User' : 'Add New User'}
              </DialogTitle>
            </DialogHeader>
            
            {/* Step Indicator */}
            <div className="flex items-center gap-2 pb-2 flex-shrink-0">
              <div className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-sm ${formStep === 1 ? 'bg-primary text-primary-foreground' : 'bg-muted'}`}>
                <span className="w-5 h-5 rounded-full bg-white/20 flex items-center justify-center text-xs">1</span>
                Basic Info
              </div>
              <ChevronRight className="w-4 h-4 text-muted-foreground" />
              <div className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-sm ${formStep === 2 ? 'bg-primary text-primary-foreground' : 'bg-muted'}`}>
                <span className="w-5 h-5 rounded-full bg-white/20 flex items-center justify-center text-xs">2</span>
                Role & Org
              </div>
            </div>

            <ScrollArea className="flex-1 overflow-y-auto pr-4" style={{ maxHeight: 'calc(90vh - 180px)' }}>
              {formStep === 1 && (
                <div className="space-y-3 py-1">
                  {/* Avatar Selection */}
                  <div className="space-y-2">
                    <Label className="text-xs">Profile Photo</Label>
                    <div className="flex items-center gap-3">
                      {/* Current/Selected Avatar */}
                      <div 
                        className="relative cursor-pointer group flex-shrink-0"
                        onClick={() => avatarInputRef.current?.click()}
                      >
                        <Avatar className="h-14 w-14 border-2 border-dashed border-muted-foreground/50 group-hover:border-primary">
                          {avatarPreview ? (
                            <AvatarImage src={avatarPreview} />
                          ) : (
                            <AvatarFallback className="bg-muted">
                              <Camera className="w-5 h-5 text-muted-foreground" />
                            </AvatarFallback>
                          )}
                        </Avatar>
                        <div className="absolute -bottom-1 -right-1 bg-primary text-primary-foreground rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity">
                          <Camera className="w-3 h-3" />
                        </div>
                        <input type="file" accept="image/*" className="hidden" ref={avatarInputRef} onChange={handleAvatarChange} />
                      </div>
                      
                      {/* Default Avatar Options */}
                      <div className="flex items-center gap-1.5">
                        <span className="text-xs text-muted-foreground mr-1">or choose:</span>
                        {[
                          { id: 'avatar1', url: 'https://api.dicebear.com/7.x/avataaars/svg?seed=Felix&backgroundColor=b6e3f4', label: 'Avatar 1' },
                          { id: 'avatar2', url: 'https://api.dicebear.com/7.x/avataaars/svg?seed=Aneka&backgroundColor=ffd5dc', label: 'Avatar 2' },
                          { id: 'avatar3', url: 'https://api.dicebear.com/7.x/avataaars/svg?seed=Leo&backgroundColor=c0aede', label: 'Avatar 3' },
                          { id: 'avatar4', url: 'https://api.dicebear.com/7.x/avataaars/svg?seed=Mia&backgroundColor=d1f4d1', label: 'Avatar 4' },
                          { id: 'avatar5', url: 'https://api.dicebear.com/7.x/avataaars/svg?seed=Jack&backgroundColor=ffdfba', label: 'Avatar 5' },
                          { id: 'avatar6', url: 'https://api.dicebear.com/7.x/avataaars/svg?seed=Sophie&backgroundColor=bae1ff', label: 'Avatar 6' },
                        ].map((avatar) => (
                          <button
                            key={avatar.id}
                            type="button"
                            onClick={() => {
                              setAvatarPreview(avatar.url);
                              setFormData(prev => ({ ...prev, avatar: avatar.url }));
                            }}
                            className={`rounded-full p-0.5 border-2 transition-all hover:scale-110 ${
                              avatarPreview === avatar.url 
                                ? 'border-primary ring-2 ring-primary/30' 
                                : 'border-transparent hover:border-muted-foreground/30'
                            }`}
                            title={avatar.label}
                          >
                            <Avatar className="h-8 w-8">
                              <AvatarImage src={avatar.url} />
                            </Avatar>
                          </button>
                        ))}
                        {avatarPreview && (
                          <button
                            type="button"
                            onClick={() => {
                              setAvatarPreview(null);
                              setFormData(prev => ({ ...prev, avatar: null }));
                            }}
                            className="ml-1 p-1 rounded-full hover:bg-muted text-muted-foreground hover:text-destructive transition-colors"
                            title="Remove avatar"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Name + Employee ID Row */}
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1.5">
                      <Label className="text-xs">Full Name *</Label>
                      <div className="relative">
                        <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                        <Input
                          value={formData.name}
                          onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                          placeholder="Enter full name"
                          className="pl-10"
                          data-testid="user-name-input"
                        />
                      </div>
                    </div>
                    <div className="space-y-1.5">
                      <Label className="text-xs">Employee ID</Label>
                      <div className="relative">
                        <IdCard className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                        <Input
                          value={formData.employee_id}
                          onChange={(e) => {
                            const empId = e.target.value;
                            setFormData(prev => ({ ...prev, employee_id: empId }));
                            validateEmployeeId(empId, editingUserId);
                          }}
                          placeholder="Enter employee ID"
                          className={`pl-10 ${validation.employee_id.available === false ? 'border-red-500 focus-visible:ring-red-500' : validation.employee_id.available === true ? 'border-green-500 focus-visible:ring-green-500' : ''}`}
                          data-testid="user-employee-id-input"
                        />
                        {validation.employee_id.checking && (
                          <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 animate-spin text-muted-foreground" />
                        )}
                        {!validation.employee_id.checking && validation.employee_id.available === true && formData.employee_id && (
                          <CheckCircle2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-green-500" />
                        )}
                        {!validation.employee_id.checking && validation.employee_id.available === false && (
                          <XCircle className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-red-500" />
                        )}
                      </div>
                      {validation.employee_id.available === false && (
                        <p className="text-xs text-red-500">{validation.employee_id.message}</p>
                      )}
                    </div>
                  </div>

                  {/* Email + Mobile Row */}
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1.5">
                      <Label className="text-xs">Email</Label>
                      <div className="relative">
                        <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                        <Input
                          type="email"
                          value={formData.email}
                          onChange={(e) => {
                            const email = e.target.value;
                            setFormData(prev => ({ ...prev, email }));
                            validateEmail(email, editingUserId);
                          }}
                          placeholder="Your email id"
                          className={`pl-10 ${validation.email.available === false ? 'border-red-500 focus-visible:ring-red-500' : validation.email.available === true ? 'border-green-500 focus-visible:ring-green-500' : ''}`}
                        />
                        {validation.email.checking && (
                          <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 animate-spin text-muted-foreground" />
                        )}
                        {!validation.email.checking && validation.email.available === true && (
                          <CheckCircle2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-green-500" />
                        )}
                        {!validation.email.checking && validation.email.available === false && (
                          <XCircle className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-red-500" />
                        )}
                      </div>
                      {validation.email.available === false && (
                        <p className="text-xs text-red-500">{validation.email.message}</p>
                      )}
                    </div>
                    <div className="space-y-1.5">
                      <Label className="text-xs">Mobile Number *</Label>
                      <div className="relative">
                        <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                        <Input
                          value={formData.mobile_number}
                          onChange={(e) => {
                            const mobile = e.target.value.replace(/\D/g, '').slice(0, 10);
                            setFormData(prev => ({ ...prev, mobile_number: mobile }));
                            validateMobile(mobile, editingUserId);
                          }}
                          placeholder="Enter 10-digit mobile number"
                          className={`pl-10 ${validation.mobile.available === false ? 'border-red-500 focus-visible:ring-red-500' : validation.mobile.available === true ? 'border-green-500 focus-visible:ring-green-500' : ''}`}
                          maxLength={10}
                          required
                        />
                        {validation.mobile.checking && (
                          <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 animate-spin text-muted-foreground" />
                        )}
                        {!validation.mobile.checking && validation.mobile.available === true && formData.mobile_number.length === 10 && (
                          <CheckCircle2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-green-500" />
                        )}
                        {!validation.mobile.checking && validation.mobile.available === false && (
                          <XCircle className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-red-500" />
                        )}
                      </div>
                      {validation.mobile.available === false && (
                        <p className="text-xs text-red-500">{validation.mobile.message}</p>
                      )}
                    </div>
                  </div>

                  {/* Gender Selection - Icon Based */}
                  {/* Gender + DOB + DOJ Row */}
                  <div className="grid grid-cols-3 gap-3">
                    <div className="space-y-1.5">
                      <Label className="text-xs">Gender</Label>
                      <div className="flex gap-1">
                        {/* Male */}
                        <button
                          type="button"
                          onClick={() => setFormData(prev => ({ ...prev, gender: 'male' }))}
                          className={`flex-1 flex flex-col items-center gap-1 py-2 px-1 rounded-md border transition-all duration-200 ${
                            formData.gender === 'male' 
                              ? 'border-blue-500 bg-blue-50 dark:bg-blue-950/30 text-blue-600' 
                              : 'border-muted hover:border-blue-300'
                          }`}
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4">
                            <circle cx="10" cy="14" r="5"/>
                            <path d="M19 5l-5.4 5.4"/>
                            <path d="M15 5h4v4"/>
                          </svg>
                          <span className="text-[10px] font-medium">Male</span>
                        </button>

                        {/* Female */}
                        <button
                          type="button"
                          onClick={() => setFormData(prev => ({ ...prev, gender: 'female' }))}
                          className={`flex-1 flex flex-col items-center gap-1 py-2 px-1 rounded-md border transition-all duration-200 ${
                            formData.gender === 'female' 
                              ? 'border-pink-500 bg-pink-50 dark:bg-pink-950/30 text-pink-600' 
                              : 'border-muted hover:border-pink-300'
                          }`}
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4">
                            <circle cx="12" cy="8" r="5"/>
                            <path d="M12 13v8"/>
                            <path d="M9 18h6"/>
                          </svg>
                          <span className="text-[10px] font-medium">Female</span>
                        </button>

                        {/* Other */}
                        <button
                          type="button"
                          onClick={() => setFormData(prev => ({ ...prev, gender: 'other' }))}
                          className={`flex-1 flex flex-col items-center gap-1 py-2 px-1 rounded-md border transition-all duration-200 ${
                            formData.gender === 'other' 
                              ? 'border-purple-500 bg-purple-50 dark:bg-purple-950/30 text-purple-600' 
                              : 'border-muted hover:border-purple-300'
                          }`}
                        >
                          <User className="w-4 h-4" />
                          <span className="text-[10px] font-medium">Other</span>
                        </button>
                      </div>
                    </div>

                    <div className="space-y-1.5">
                      <Label className="text-xs">Date of Birth</Label>
                      <div className="relative">
                        <Calendar className="absolute left-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
                        <Input
                          type="date"
                          value={formData.date_of_birth}
                          onChange={(e) => setFormData(prev => ({ ...prev, date_of_birth: e.target.value }))}
                          className="pl-8 text-sm"
                        />
                      </div>
                    </div>

                    <div className="space-y-1.5">
                      <Label className="text-xs">Date of Joining</Label>
                      <div className="relative">
                        <Calendar className="absolute left-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
                        <Input
                          type="date"
                          value={formData.date_of_joining}
                          onChange={(e) => setFormData(prev => ({ ...prev, date_of_joining: e.target.value }))}
                          className="pl-8 text-sm"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {formStep === 2 && (
                <div className="space-y-5 py-2">
                  {/* Section 1: Role & Status */}
                  <div className="space-y-4">
                    <div className="flex items-center gap-2 text-sm font-semibold text-foreground">
                      <div className="w-6 h-6 rounded-full bg-primary/10 flex items-center justify-center">
                        <span className="text-xs text-primary font-bold">1</span>
                      </div>
                      {t('users.roleAndStatus', 'Role & Status')}
                    </div>
                    
                    <div className="grid grid-cols-3 gap-4">
                      {/* Role Selection - Uses dynamic roles from API */}
                      <div className="space-y-2 col-span-2">
                        <Label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                          {t('users.role', 'Role')} *
                        </Label>
                        <Select value={formData.role} onValueChange={(v) => setFormData(prev => ({ ...prev, role: v }))}>
                          <SelectTrigger className="h-11" data-testid="user-role-select">
                            <SelectValue placeholder="Select a role" />
                          </SelectTrigger>
                          <SelectContent>
                            {roles.length > 0 ? (
                              roles.map((role) => (
                                <SelectItem key={role.value} value={role.value}>
                                  <div className="flex items-center gap-2">
                                    <span>{role.label}</span>
                                  </div>
                                </SelectItem>
                              ))
                            ) : (
                              ROLES.map((role) => (
                                <SelectItem key={role.id} value={role.id}>
                                  <div className="flex items-center gap-2">
                                    <role.icon className={`w-4 h-4 ${role.color}`} />
                                    <span>{role.name}</span>
                                  </div>
                                </SelectItem>
                              ))
                            )}
                          </SelectContent>
                        </Select>
                        <p className="text-xs text-muted-foreground">
                          {roles.find(r => r.value === formData.role)?.description || 
                           ROLES.find(r => r.id === formData.role)?.description || ''}
                        </p>
                      </div>
                    </div>

                    {/* Status, Welcome Email, OTP Login Row */}
                    <div className={`grid gap-3 ${isEditing ? 'grid-cols-2' : 'grid-cols-3'}`}>
                      {/* Active Status */}
                      <div className="space-y-1">
                        <Label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                          Status
                        </Label>
                        <div 
                          className={`h-10 flex items-center justify-between px-3 rounded-md border cursor-pointer transition-colors ${
                            formData.is_active 
                              ? 'bg-green-500/10 border-green-500/30' 
                              : 'bg-red-500/10 border-red-500/30'
                          }`}
                          onClick={() => setFormData(prev => ({ ...prev, is_active: !prev.is_active }))}
                        >
                          <div className="flex items-center gap-2">
                            <div className={`w-2 h-2 rounded-full ${formData.is_active ? 'bg-green-500' : 'bg-red-500'}`} />
                            <span className={`text-xs font-medium ${formData.is_active ? 'text-green-700' : 'text-red-700'}`}>
                              {formData.is_active ? 'Active' : 'Inactive'}
                            </span>
                          </div>
                          <Switch
                            checked={formData.is_active}
                            onCheckedChange={(checked) => setFormData(prev => ({ ...prev, is_active: checked }))}
                            className="scale-75"
                          />
                        </div>
                      </div>

                      {/* Send Welcome Email - Only for new users */}
                      {!isEditing && (
                        <div className="space-y-1">
                          <Label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                            Welcome Email
                          </Label>
                          <div 
                            className={`h-10 flex items-center justify-between px-3 rounded-md border cursor-pointer transition-colors ${
                              formData.send_welcome_email 
                                ? 'bg-blue-500/10 border-blue-500/30' 
                                : 'bg-muted/50 border-border'
                            }`}
                            onClick={() => setFormData(prev => ({ ...prev, send_welcome_email: !prev.send_welcome_email }))}
                          >
                            <div className="flex items-center gap-2">
                              <Mail className={`w-3 h-3 ${formData.send_welcome_email ? 'text-blue-500' : 'text-muted-foreground'}`} />
                              <span className={`text-xs font-medium ${formData.send_welcome_email ? 'text-blue-700' : 'text-muted-foreground'}`}>
                                {formData.send_welcome_email ? 'Yes' : 'No'}
                              </span>
                            </div>
                            <Switch
                              checked={formData.send_welcome_email}
                              onCheckedChange={(checked) => setFormData(prev => ({ ...prev, send_welcome_email: checked }))}
                              className="scale-75"
                            />
                          </div>
                        </div>
                      )}

                      {/* Enable OTP Login */}
                      <div className="space-y-1">
                        <Label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                          OTP Login
                        </Label>
                        <div 
                          className={`h-10 flex items-center justify-between px-3 rounded-md border cursor-pointer transition-colors ${
                            !formData.mobile_number || formData.mobile_number.length !== 10
                              ? 'bg-muted/30 border-border opacity-50 cursor-not-allowed'
                              : formData.otp_login_enabled 
                                ? 'bg-orange-500/10 border-orange-500/30' 
                                : 'bg-muted/50 border-border'
                          }`}
                          onClick={() => {
                            if (formData.mobile_number && formData.mobile_number.length === 10) {
                              setFormData(prev => ({ ...prev, otp_login_enabled: !prev.otp_login_enabled }));
                            }
                          }}
                          title={!formData.mobile_number || formData.mobile_number.length !== 10 ? 'Add mobile number first' : ''}
                        >
                          <div className="flex items-center gap-2">
                            <Phone className={`w-3 h-3 ${formData.otp_login_enabled ? 'text-orange-500' : 'text-muted-foreground'}`} />
                            <span className={`text-xs font-medium ${formData.otp_login_enabled ? 'text-orange-700' : 'text-muted-foreground'}`}>
                              {formData.otp_login_enabled ? 'Enabled' : 'Disabled'}
                            </span>
                          </div>
                          <Switch
                            checked={formData.otp_login_enabled}
                            onCheckedChange={(checked) => setFormData(prev => ({ ...prev, otp_login_enabled: checked }))}
                            disabled={!formData.mobile_number || formData.mobile_number.length !== 10}
                            className="scale-75"
                          />
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Section 2: Organization - Only for non-admin roles */}
                  {formData.role !== 'admin' && (
                    <>
                      <Separator />
                      
                      <div className="space-y-4">
                        <div className="flex items-center gap-2 text-sm font-semibold text-foreground">
                          <div className="w-6 h-6 rounded-full bg-primary/10 flex items-center justify-center">
                            <span className="text-xs text-primary font-bold">2</span>
                          </div>
                          {t('users.organization', 'Organization')}
                        </div>

                        <div className="grid grid-cols-2 gap-4">
                          {/* Org Position */}
                          <div className="space-y-2">
                            <Label className="text-xs font-medium text-muted-foreground uppercase tracking-wide flex items-center gap-1.5">
                              <Network className="w-3.5 h-3.5" />
                              {t('users.orgPosition', 'Position')}
                            </Label>
                            <Select 
                              value={formData.org_entry_id || "none"} 
                              onValueChange={(v) => setFormData(prev => ({ ...prev, org_entry_id: v === "none" ? "" : v }))}
                            >
                              <SelectTrigger className="h-11" data-testid="user-org-position">
                                <SelectValue placeholder={t('users.selectPosition', 'Select position')} />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="none">
                                  <span className="text-muted-foreground">{t('users.notAssigned', 'Not Assigned')}</span>
                                </SelectItem>
                                {orgLevels.map(level => (
                                  <React.Fragment key={level.id}>
                                    <div className="px-2 py-1.5 text-xs font-semibold text-muted-foreground bg-muted/50 sticky top-0">
                                      L{level.level_number}: {level.level_name}
                                    </div>
                                    {orgEntries.filter(e => e.level_id === level.id && e.status === 'active').map(entry => (
                                      <SelectItem key={entry.id} value={entry.id}>
                                        <span className="pl-2">{entry.entry_name}</span>
                                        <span className="text-xs text-muted-foreground ml-1">({entry.entry_code})</span>
                                      </SelectItem>
                                    ))}
                                  </React.Fragment>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>

                          {/* Reporting Manager */}
                          <div className="space-y-2">
                            <Label className="text-xs font-medium text-muted-foreground uppercase tracking-wide flex items-center gap-1.5">
                              <UserCheck className="w-3.5 h-3.5" />
                              {t('users.reportingManager', 'Reports To')}
                            </Label>
                            <Select 
                              value={formData.manager_id || "none"} 
                              onValueChange={(v) => setFormData(prev => ({ ...prev, manager_id: v === "none" ? "" : v }))}
                            >
                              <SelectTrigger className="h-11" data-testid="user-manager-select">
                                <SelectValue placeholder={t('users.selectManager', 'Select manager')} />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="none">
                                  <span className="text-muted-foreground">{t('users.noManager', 'No Manager')}</span>
                                </SelectItem>
                                {managers.map(m => (
                                  <SelectItem key={m.id} value={m.id}>
                                    <div className="flex items-center gap-2">
                                      <Avatar className="h-5 w-5">
                                        <AvatarFallback className="text-xs">{m.name?.charAt(0)}</AvatarFallback>
                                      </Avatar>
                                      <span>{m.name}</span>
                                      <Badge variant="outline" className="text-xs scale-90">{m.role.replace('_', ' ')}</Badge>
                                    </div>
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                        </div>

                        {/* Assigned Stores - Multi-select with search */}
                        {stores.length > 0 && (
                          <div className="space-y-2">
                            <Label className="text-xs font-medium text-muted-foreground uppercase tracking-wide flex items-center gap-1.5">
                              <Store className="w-3.5 h-3.5" />
                              {t('users.assignedStores', 'Assigned Stores')}
                              {formData.assigned_store_ids.length > 0 && (
                                <Badge variant="secondary" className="ml-1 text-xs">
                                  {formData.assigned_store_ids.length} {t('common.selected', 'selected')}
                                </Badge>
                              )}
                            </Label>
                            <Popover>
                              <PopoverTrigger asChild>
                                <Button
                                  variant="outline"
                                  role="combobox"
                                  className="w-full h-11 justify-between font-normal"
                                  data-testid="user-stores-select"
                                >
                                  {formData.assigned_store_ids.length > 0 ? (
                                    <div className="flex items-center gap-1 flex-wrap max-w-[90%] overflow-hidden">
                                      {formData.assigned_store_ids.slice(0, 2).map(storeId => {
                                        const store = stores.find(s => s.id === storeId);
                                        return store ? (
                                          <Badge key={storeId} variant="secondary" className="text-xs">
                                            {store.store_name}
                                          </Badge>
                                        ) : null;
                                      })}
                                      {formData.assigned_store_ids.length > 2 && (
                                        <Badge variant="outline" className="text-xs">
                                          +{formData.assigned_store_ids.length - 2} {t('common.more', 'more')}
                                        </Badge>
                                      )}
                                    </div>
                                  ) : (
                                    <span className="text-muted-foreground">{t('users.selectStores', 'Select stores...')}</span>
                                  )}
                                  <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                                </Button>
                              </PopoverTrigger>
                              <PopoverContent className="w-[400px] p-0" align="start">
                                <Command>
                                  <CommandInput placeholder={t('users.searchStores', 'Search stores...')} />
                                  <CommandList>
                                    <CommandEmpty>{t('common.noResults', 'No stores found.')}</CommandEmpty>
                                    <CommandGroup>
                                      {/* Select All */}
                                      <CommandItem
                                        onSelect={() => {
                                          const activeStores = stores.filter(s => s.status === 'active');
                                          if (formData.assigned_store_ids.length === activeStores.length) {
                                            setFormData(prev => ({ ...prev, assigned_store_ids: [] }));
                                          } else {
                                            setFormData(prev => ({ ...prev, assigned_store_ids: activeStores.map(s => s.id) }));
                                          }
                                        }}
                                        className="border-b"
                                      >
                                        <div className="flex items-center gap-2 w-full">
                                          <Checkbox 
                                            checked={formData.assigned_store_ids.length === stores.filter(s => s.status === 'active').length}
                                            className="pointer-events-none"
                                          />
                                          <span className="font-medium">{t('common.selectAll', 'Select All')}</span>
                                          <span className="text-xs text-muted-foreground ml-auto">
                                            ({stores.filter(s => s.status === 'active').length} {t('common.stores', 'stores')})
                                          </span>
                                        </div>
                                      </CommandItem>
                                      {stores.filter(s => s.status === 'active').map(store => (
                                        <CommandItem
                                          key={store.id}
                                          onSelect={() => handleStoreToggle(store.id)}
                                        >
                                          <div className="flex items-center gap-3 w-full">
                                            <Checkbox 
                                              checked={formData.assigned_store_ids.includes(store.id)}
                                              className="pointer-events-none"
                                            />
                                            <div className="flex-1 min-w-0">
                                              <p className="text-sm font-medium truncate">{store.store_name}</p>
                                              <p className="text-xs text-muted-foreground">
                                                {store.store_code} • {store.city || 'No city'}
                                              </p>
                                            </div>
                                          </div>
                                        </CommandItem>
                                      ))}
                                    </CommandGroup>
                                  </CommandList>
                                </Command>
                              </PopoverContent>
                            </Popover>
                            
                            {/* Selected stores chips */}
                            {formData.assigned_store_ids.length > 0 && (
                              <div className="flex flex-wrap gap-1.5 mt-2">
                                {formData.assigned_store_ids.map(storeId => {
                                  const store = stores.find(s => s.id === storeId);
                                  return store ? (
                                    <Badge 
                                      key={storeId} 
                                      variant="secondary" 
                                      className="text-xs pl-2 pr-1 py-1 flex items-center gap-1"
                                    >
                                      <Store className="w-3 h-3" />
                                      {store.store_name}
                                      <button
                                        type="button"
                                        onClick={() => handleStoreToggle(storeId)}
                                        className="ml-1 hover:bg-muted rounded-full p-0.5"
                                      >
                                        <X className="w-3 h-3" />
                                      </button>
                                    </Badge>
                                  ) : null;
                                })}
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    </>
                  )}

                  {/* Admin Role Notice */}
                  {formData.role === 'admin' && (
                    <div className="bg-amber-500/10 border border-amber-500/20 p-4 rounded-lg">
                      <div className="flex items-start gap-3">
                        <Crown className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
                        <div>
                          <p className="font-medium text-sm">{t('users.adminRole', 'Administrator Role')}</p>
                          <p className="text-xs text-muted-foreground mt-1">
                            {t('users.adminRoleDesc', 'Admins have full system access and are not assigned to the organization hierarchy.')}
                          </p>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </ScrollArea>

            <DialogFooter className="flex-shrink-0 pt-4 border-t">
              <div className="flex justify-between w-full">
                {formStep === 1 ? (
                  <>
                    <Button variant="outline" onClick={() => setUserDialogOpen(false)}>{t('common.cancel', 'Cancel')}</Button>
                    <Button onClick={() => validateStep(1) && setFormStep(2)}>
                      {t('common.next', 'Next')}
                      <ChevronRight className="w-4 h-4 ml-1" />
                    </Button>
                  </>
                ) : (
                  <>
                    <Button variant="outline" onClick={() => setFormStep(1)}>
                      <ChevronLeft className="w-4 h-4 mr-1" />
                      {t('common.back', 'Back')}
                    </Button>
                    <Button onClick={handleSaveUser} disabled={saving}>
                      {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                      {isEditing ? t('management.editUser', 'Update User') : t('management.createUser', 'Create User')}
                    </Button>
                  </>
                )}
              </div>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* View User Dialog */}
        <Dialog open={viewUserDialogOpen} onOpenChange={setViewUserDialogOpen}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>{t('management.userDetails', 'User Details')}</DialogTitle>
            </DialogHeader>
            {viewingUser && (
              <div className="space-y-4">
                <div className="flex items-center gap-4">
                  <Avatar className="h-16 w-16">
                    <AvatarImage src={viewingUser.avatar} />
                    <AvatarFallback className={getRoleInfo(viewingUser.role).bgColor}>
                      {viewingUser.name?.charAt(0)?.toUpperCase()}
                    </AvatarFallback>
                  </Avatar>
                  <div>
                    <h3 className="font-semibold text-lg">{viewingUser.name}</h3>
                    <p className="text-sm text-muted-foreground">{viewingUser.email}</p>
                    <Badge variant="outline" className={`mt-1 ${getRoleInfo(viewingUser.role).bgColor}`}>
                      {getRoleInfo(viewingUser.role).name}
                    </Badge>
                  </div>
                </div>
                <Separator />
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div>
                    <p className="text-muted-foreground text-xs">Employee ID</p>
                    <p className="font-medium">{viewingUser.employee_id || '-'}</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground text-xs">Mobile</p>
                    <p className="font-medium">{viewingUser.mobile_number || '-'}</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground text-xs">Gender</p>
                    <p className="font-medium capitalize">{viewingUser.gender || '-'}</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground text-xs">Date of Birth</p>
                    <p className="font-medium">{viewingUser.date_of_birth || '-'}</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground text-xs">Date of Joining</p>
                    <p className="font-medium">{viewingUser.date_of_joining || '-'}</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground text-xs">Org Position</p>
                    <p className="font-medium">{getOrgEntryName(viewingUser.org_entry_id) || '-'}</p>
                  </div>
                </div>
                {viewingUser.assigned_store_ids?.length > 0 && (
                  <>
                    <Separator />
                    <div>
                      <p className="text-muted-foreground text-xs mb-2">Assigned Stores ({viewingUser.assigned_store_ids.length})</p>
                      <div className="flex flex-wrap gap-1">
                        {viewingUser.assigned_store_ids.map(sid => {
                          const store = stores.find(s => s.id === sid);
                          return store ? (
                            <Badge key={sid} variant="secondary" className="text-xs">
                              {store.store_name}
                            </Badge>
                          ) : null;
                        })}
                      </div>
                    </div>
                  </>
                )}
              </div>
            )}
            <DialogFooter>
              <Button variant="outline" onClick={() => setViewUserDialogOpen(false)}>Close</Button>
              <Button onClick={() => {
                setViewUserDialogOpen(false);
                openEditDialog(viewingUser);
              }}>
                <Edit className="w-4 h-4 mr-2" />
                Edit
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Delete Confirmation */}
        <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>{t('management.deleteUser', 'Delete User')}</AlertDialogTitle>
              <AlertDialogDescription>
                {t('management.confirmDeleteUser', 'This will permanently delete the user and all their data. This action cannot be undone.')}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel onClick={() => setUserToDelete(null)}>{t('common.cancel', 'Cancel')}</AlertDialogCancel>
              <AlertDialogAction onClick={handleDeleteUser} className="bg-destructive text-destructive-foreground">
                {t('common.delete', 'Delete')}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* Reset Password Confirmation Dialog */}
        <AlertDialog open={resetPasswordDialogOpen} onOpenChange={setResetPasswordDialogOpen}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle className="flex items-center gap-2">
                <KeyRound className="w-5 h-5 text-amber-500" />
                {t('management.resetPassword', 'Reset Password')}
              </AlertDialogTitle>
              <AlertDialogDescription>
                {t('management.resetPasswordConfirm', 'A new temporary password will be generated and sent via email. The user will be required to change their password on their next login.')}</AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel onClick={() => setUserToReset(null)} disabled={resettingPassword}>
                Cancel
              </AlertDialogCancel>
              <AlertDialogAction 
                onClick={handleResetPassword} 
                disabled={resettingPassword}
                className="bg-amber-500 hover:bg-amber-600 text-white"
              >
                {resettingPassword ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Resetting...
                  </>
                ) : (
                  <>
                    <KeyRound className="w-4 h-4 mr-2" />
                    Reset Password
                  </>
                )}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

          </TabsContent>

          {/* Roles Tab */}
          <TabsContent value="roles" className="mt-6 space-y-6">
            {/* Roles Header */}
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-xl font-semibold">Role Management</h2>
                <p className="text-sm text-muted-foreground">Define roles and their permissions</p>
              </div>
              <Button onClick={() => openRoleDialog()} data-testid="add-role-btn">
                <Plus className="w-4 h-4 mr-2" />
                Create Role
              </Button>
            </div>

            {/* Roles Grid */}
            {rolesLoading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-primary" />
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {roles.map((role) => (
                  <Card key={role.value} className="hover:shadow-md transition-shadow">
                    <CardHeader className="pb-2">
                      <div className="flex items-start justify-between">
                        <div>
                          <CardTitle className="text-lg flex items-center gap-2">
                            <Shield className="w-5 h-5" style={{ color: branding.primary_color }} />
                            {role.label}
                          </CardTitle>
                          <Badge variant={role.is_system ? 'secondary' : 'outline'} className="mt-1">
                            {role.is_system ? 'System Role' : 'Custom Role'}
                          </Badge>
                        </div>
                        <div className="flex gap-1">
                          <Button variant="ghost" size="sm" onClick={() => openRoleDialog(role)}>
                            <Edit className="w-4 h-4" />
                          </Button>
                          {!role.is_system && (
                            <Button variant="ghost" size="sm" onClick={() => deleteRole(role.value)}>
                              <Trash2 className="w-4 h-4 text-destructive" />
                            </Button>
                          )}
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent>
                      <p className="text-sm text-muted-foreground mb-3">
                        {role.description || 'No description'}
                      </p>
                      <div className="text-xs">
                        <span className="font-medium">Permissions:</span>
                        <div className="flex flex-wrap gap-1 mt-1">
                          {role.permissions && typeof role.permissions === 'object' ? (
                            Object.keys(role.permissions).length > 0 ? (
                              Object.keys(role.permissions).slice(0, 4).map(moduleId => (
                                <Badge key={moduleId} variant="outline" className="text-xs">
                                  {PERMISSION_MODULES.find(m => m.id === moduleId)?.name || moduleId}
                                </Badge>
                              ))
                            ) : (
                              <span className="text-muted-foreground">Full access (Admin)</span>
                            )
                          ) : (
                            <span className="text-muted-foreground">
                              {Array.isArray(role.permissions) && role.permissions.includes('all') ? 'Full access' : 'Default permissions'}
                            </span>
                          )}
                          {role.permissions && typeof role.permissions === 'object' && Object.keys(role.permissions).length > 4 && (
                            <Badge variant="outline" className="text-xs">+{Object.keys(role.permissions).length - 4} more</Badge>
                          )}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}

            {/* Role Dialog */}
            <Dialog open={roleDialogOpen} onOpenChange={setRoleDialogOpen}>
              <DialogContent className="max-w-3xl max-h-[90vh] flex flex-col">
                <DialogHeader>
                  <DialogTitle>{editingRole ? 'Edit Role' : 'Create New Role'}</DialogTitle>
                  <DialogDescription>
                    {editingRole ? 'Modify role permissions and settings' : 'Create a custom role with specific permissions'}
                  </DialogDescription>
                </DialogHeader>
                
                <ScrollArea className="flex-1 overflow-y-auto pr-4" style={{ maxHeight: 'calc(90vh - 160px)' }}>
                  <div className="space-y-6 py-4">
                    {/* Basic Info */}
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Role Name *</Label>
                        <Input
                          value={roleFormData.label}
                          onChange={(e) => setRoleFormData(prev => ({ ...prev, label: e.target.value }))}
                          placeholder="e.g., Course Admin"
                          disabled={editingRole?.is_system}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Role ID</Label>
                        <Input
                          value={roleFormData.value || roleFormData.label?.toLowerCase().replace(/\s+/g, '_') || ''}
                          onChange={(e) => setRoleFormData(prev => ({ ...prev, value: e.target.value.toLowerCase().replace(/\s+/g, '_') }))}
                          placeholder="Auto-generated"
                          disabled={!!editingRole}
                          className="font-mono text-sm"
                        />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label>Description</Label>
                      <Input
                        value={roleFormData.description}
                        onChange={(e) => setRoleFormData(prev => ({ ...prev, description: e.target.value }))}
                        placeholder="Brief description of this role's purpose"
                      />
                    </div>

                    {/* Permissions */}
                    <div className="space-y-4">
                      <div>
                        <h3 className="text-lg font-semibold">Permissions</h3>
                        <p className="text-sm text-muted-foreground">Select which modules and features this role can access</p>
                      </div>

                      <div className="space-y-4">
                        {PERMISSION_MODULES.map((module) => (
                          <Card key={module.id} className={isModuleEnabled(module.id) ? 'border-primary/50 bg-primary/5' : ''}>
                            <CardHeader className="py-3">
                              <div className="flex items-center gap-3">
                                <Checkbox
                                  id={`module-${module.id}`}
                                  checked={isModuleEnabled(module.id)}
                                  onCheckedChange={(checked) => handleModulePermissionChange(module.id, checked)}
                                />
                                <div className="flex-1">
                                  <Label htmlFor={`module-${module.id}`} className="text-base font-medium cursor-pointer">
                                    {module.name}
                                  </Label>
                                  <p className="text-xs text-muted-foreground">{module.description}</p>
                                </div>
                              </div>
                            </CardHeader>
                            {isModuleEnabled(module.id) && (
                              <CardContent className="pt-0 pb-3">
                                <div className="ml-6 grid grid-cols-2 md:grid-cols-3 gap-2">
                                  {module.permissions.map((perm) => (
                                    <div key={perm.id} className="flex items-center gap-2">
                                      <Checkbox
                                        id={`perm-${module.id}-${perm.id}`}
                                        checked={isFeatureEnabled(module.id, perm.id)}
                                        onCheckedChange={(checked) => handleFeaturePermissionChange(module.id, perm.id, checked)}
                                      />
                                      <Label 
                                        htmlFor={`perm-${module.id}-${perm.id}`} 
                                        className="text-sm cursor-pointer"
                                        title={perm.description}
                                      >
                                        {perm.name}
                                      </Label>
                                    </div>
                                  ))}
                                </div>
                              </CardContent>
                            )}
                          </Card>
                        ))}
                      </div>
                    </div>
                  </div>
                </ScrollArea>

                <DialogFooter className="pt-4 border-t">
                  <Button variant="outline" onClick={() => setRoleDialogOpen(false)}>Cancel</Button>
                  <Button onClick={saveRole}>
                    {editingRole ? 'Save Changes' : 'Create Role'}
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </TabsContent>
        </Tabs>
      </div>
    </DashboardLayout>
  );
};

export default UserManagement;
